import type { Me } from '@myrewards/contracts';
import type { QueryClient } from '@tanstack/react-query';
import { redirect } from 'react-router';
import { isNotSignedIn, meQuery, signInPagePath } from '../api';
import { setMotionPreference } from '../design/motion';

/**
 * Loads the signed-in person before a page renders. Without a session (401) it redirects to
 * `/sign-in?returnTo=<path+search>`, so the person comes back to the same place; any other
 * failure reaches the route's error boundary. Also applies the motion preference before paint.
 */
export async function requireMe(
  queryClient: QueryClient,
  request: Request,
  returnTo?: string,
): Promise<Me> {
  let me: Me;
  try {
    // Cached `me` is used as it is; only the first page load fetches it.
    me = await queryClient.query({ ...meQuery, staleTime: 'static' });
  } catch (error) {
    if (isNotSignedIn(error)) {
      const url = new URL(request.url);
      // eslint-disable-next-line @typescript-eslint/only-throw-error -- React Router loaders redirect by throwing a Response.
      throw redirect(signInPagePath(returnTo ?? `${url.pathname}${url.search}`));
    }
    throw error;
  }
  setMotionPreference(me.preferences.motion);
  return me;
}
